/**
 * Volcado de metadatos DICOM desde un DataSet de dicom-parser / cornerstone.
 * Omite el binario de Pixel Data; secuencias se expanden un nivel.
 */

import { resolveTransferSyntax } from './transferSyntax'

/** Nombres conocidos (subset útil; el resto se muestra como (gggg,eeee)). */
export const DICOM_TAG_NAMES = {
  x00020000: 'File Meta Information Group Length',
  x00020001: 'File Meta Information Version',
  x00020002: 'Media Storage SOP Class UID',
  x00020003: 'Media Storage SOP Instance UID',
  x00020010: 'Transfer Syntax UID',
  x00020012: 'Implementation Class UID',
  x00020013: 'Implementation Version Name',
  x00020016: 'Source Application Entity Title',
  x00080005: 'Specific Character Set',
  x00080008: 'Image Type',
  x00080012: 'Instance Creation Date',
  x00080013: 'Instance Creation Time',
  x00080014: 'Instance Creator UID',
  x00080016: 'SOP Class UID',
  x00080018: 'SOP Instance UID',
  x00080020: 'Study Date',
  x00080021: 'Series Date',
  x00080022: 'Acquisition Date',
  x00080023: 'Content Date',
  x0008002a: 'Acquisition DateTime',
  x00080030: 'Study Time',
  x00080031: 'Series Time',
  x00080032: 'Acquisition Time',
  x00080033: 'Content Time',
  x00080050: 'Accession Number',
  x00080060: 'Modality',
  x00080070: 'Manufacturer',
  x00080080: 'Institution Name',
  x00080081: 'Institution Address',
  x00080090: 'Referring Physician Name',
  x00081010: 'Station Name',
  x00081030: 'Study Description',
  x0008103e: 'Series Description',
  x00081040: 'Institutional Department Name',
  x00081050: 'Performing Physician Name',
  x00081060: 'Name of Physician(s) Reading Study',
  x00081070: 'Operators Name',
  x00081090: 'Manufacturer Model Name',
  x00081110: 'Referenced Study Sequence',
  x00081111: 'Referenced Performed Procedure Step Sequence',
  x00081115: 'Referenced Series Sequence',
  x00081120: 'Referenced Patient Sequence',
  x00081125: 'Referenced Visit Sequence',
  x00081134: 'Referenced Stereometric Instance Sequence',
  x00081140: 'Referenced Image Sequence',
  x0008114a: 'Referenced Instance Sequence',
  x00082111: 'Derivation Description',
  x00082112: 'Source Image Sequence',
  x00082144: 'Recommended Display Frame Rate',
  x00100010: 'Patient Name',
  x00100020: 'Patient ID',
  x00100021: 'Issuer of Patient ID',
  x00100030: 'Patient Birth Date',
  x00100040: 'Patient Sex',
  x00101010: 'Patient Age',
  x00101020: 'Patient Size',
  x00101030: 'Patient Weight',
  x00102160: 'Ethnic Group',
  x00104000: 'Patient Comments',
  x00180010: 'Contrast/Bolus Agent',
  x00180015: 'Body Part Examined',
  x00180020: 'Scanning Sequence',
  x00180021: 'Sequence Variant',
  x00180022: 'Scan Options',
  x00180023: 'MR Acquisition Type',
  x00180024: 'Sequence Name',
  x00180025: 'Angio Flag',
  x00180050: 'Slice Thickness',
  x00180060: 'KVP',
  x00180080: 'Repetition Time',
  x00180081: 'Echo Time',
  x00180082: 'Inversion Time',
  x00180083: 'Number of Averages',
  x00180084: 'Imaging Frequency',
  x00180085: 'Imaged Nucleus',
  x00180086: 'Echo Numbers',
  x00180087: 'Magnetic Field Strength',
  x00180088: 'Spacing Between Slices',
  x00180089: 'Number of Phase Encoding Steps',
  x00180090: 'Data Collection Diameter',
  x00180091: 'Echo Train Length',
  x00180093: 'Percent Sampling',
  x00180094: 'Percent Phase Field of View',
  x00180095: 'Pixel Bandwidth',
  x00181000: 'Device Serial Number',
  x00181020: 'Software Versions',
  x00181030: 'Protocol Name',
  x00181040: 'Contrast/Bolus Route',
  x00181041: 'Contrast/Bolus Volume',
  x00181042: 'Contrast/Bolus Start Time',
  x00181043: 'Contrast/Bolus Stop Time',
  x00181044: 'Contrast/Bolus Total Dose',
  x00181050: 'Spatial Resolution',
  x00181060: 'Trigger Time',
  x00181063: 'Frame Time',
  x00181064: 'Cardiac Framing Type',
  x00181065: 'Frame Time Vector',
  x00181066: 'Frame Delay',
  x00181067: 'Image Trigger Delay',
  x00181068: 'Multiplex Group Time Offset',
  x00181069: 'Trigger Time Offset',
  x00181081: 'Low R-R Value',
  x00181082: 'High R-R Value',
  x00181083: 'Intervals Acquired',
  x00181084: 'Intervals Rejected',
  x00181088: 'Heart Rate',
  x00181090: 'Cardiac Number of Images',
  x00181094: 'Trigger Window',
  x00181100: 'Reconstruction Diameter',
  x00181110: 'Distance Source to Detector',
  x00181111: 'Distance Source to Patient',
  x00181114: 'Estimated Radiographic Magnification Factor',
  x00181120: 'Gantry/Detector Tilt',
  x00181130: 'Table Height',
  x00181140: 'Rotation Direction',
  x00181150: 'Exposure Time',
  x00181151: 'X-Ray Tube Current',
  x00181152: 'Exposure',
  x00181153: 'Exposure in µAs',
  x00181154: 'Average Pulse Width',
  x00181155: 'Radiation Setting',
  x00181156: 'Rectification Type',
  x0018115a: 'Radiation Mode',
  x0018115e: 'Image and Fluoroscopy Area Dose Product',
  x00181160: 'Filter Type',
  x00181161: 'Type of Filters',
  x00181162: 'Intensifier Size',
  x00181164: 'Imager Pixel Spacing',
  x00181166: 'Grid',
  x00181170: 'Generator Power',
  x00181180: 'Collimator/grid Name',
  x00181181: 'Collimator Type',
  x00181182: 'Focal Distance',
  x00181183: 'X Focus Center',
  x00181184: 'Y Focus Center',
  x00181190: 'Focal Spot(s)',
  x00181191: 'Anode Target Material',
  x00181200: 'Date of Last Calibration',
  x00181201: 'Time of Last Calibration',
  x00181210: 'Convolution Kernel',
  x00181242: 'Actual Frame Duration',
  x00181243: 'Count Rate',
  x00181244: 'Preferred Playback Sequencing',
  x00181250: 'Receive Coil Name',
  x00181251: 'Transmit Coil Name',
  x00181310: 'Acquisition Matrix',
  x00181312: 'In-plane Phase Encoding Direction',
  x00181314: 'Flip Angle',
  x00181315: 'Variable Flip Angle Flag',
  x00181316: 'SAR',
  x00181318: 'dB/dt',
  x00181400: 'Acquisition Device Processing Description',
  x00181401: 'Acquisition Device Processing Code',
  x00181402: 'Cassette Orientation',
  x00181403: 'Cassette Size',
  x00181404: 'Exposures on Plate',
  x00181405: 'Relative X-Ray Exposure',
  x00185100: 'Patient Position',
  x00185101: 'View Position',
  x00185104: 'Projection Eponymous Name Code Sequence',
  x00186000: 'Sensitivity',
  x00186011: 'Sequence of Ultrasound Regions',
  x00187000: 'Detector Temperature',
  x00187004: 'Detector Type',
  x00187005: 'Detector Configuration',
  x00187006: 'Detector Description',
  x0018700a: 'Detector ID',
  x00187016: 'Shutter Shape',
  x00187032: 'Field of View Shape',
  x00187034: 'Field of View Dimension(s)',
  x00187050: 'Filter Material',
  x00187052: 'Filter Thickness Minimum',
  x00187054: 'Filter Thickness Maximum',
  x00187060: 'Exposure Control Mode',
  x00187062: 'Exposure Control Mode Description',
  x00187064: 'Exposure Status',
  x00188150: 'Exposure Index',
  x00188151: 'Target Exposure Index',
  x00188152: 'Deviation Index',
  x00189301: 'CT Acquisition Type Sequence',
  x00189302: 'Acquisition Type',
  x00189304: 'Revolution Time',
  x00189305: 'Single Collimation Width',
  x00189306: 'Total Collimation Width',
  x00189307: 'CT Table Speed',
  x00189309: 'Table Feed per Rotation',
  x00189310: 'Spiral Pitch Factor',
  x00189311: 'CT Geometry Sequence',
  x00189312: 'Data Collection Center (Patient)',
  x00189313: 'CT Reconstruction Sequence',
  x00189314: 'Reconstruction Algorithm',
  x00189315: 'Convolution Kernel Group',
  x00189316: 'Reconstruction Field of View',
  x00189317: 'Reconstruction Target Center (Patient)',
  x00189318: 'Reconstruction Angle',
  x00189319: 'Image Filter',
  x00189320: 'CT Exposure Sequence',
  x00189321: 'Reconstruction Pixel Spacing',
  x00189322: 'Exposure Modulation Type',
  x00189323: 'Estimated Dose Saving',
  x00189324: 'CT X-Ray Details Sequence',
  x00189325: 'CT Position Sequence',
  x00189326: 'Table Position',
  x00189327: 'Exposure Time in ms',
  x00189328: 'CT Image Frame Type Sequence',
  x00189329: 'X-Ray Tube Current in mA',
  x00189330: 'Exposure in mAs',
  x00189332: 'Constant Volume Flag',
  x00189333: 'Fluoroscopy Flag',
  x00189334: 'Distance Source to Data Collection Center',
  x00189335: 'Contrast/Bolus Agent Number',
  x00189337: 'Contrast/Bolus Agent Sequence',
  x00189338: 'Contrast Administration Profile Sequence',
  x00189340: 'Convolution Kernel',
  x00189412: 'X-Ray Receptor Type',
  x00189420: 'Distance Receptor Plane to Detector Housing',
  x00189426: 'Intensifier Active Shape',
  x00189427: 'Intensifier Active Dimension(s)',
  x00189428: 'Physical Detector Size',
  x00189429: 'Position of Isocenter Relative to Table Top',
  x00189430: 'Table Height',
  x00189432: 'Table Traverse',
  x00189433: 'Table Vertical Increment',
  x00189434: 'Table Lateral Increment',
  x00189435: 'Table Longitudinal Increment',
  x00189436: 'Table Angle',
  x00189437: 'Table Type',
  x00189438: 'Rotation Angle',
  x00189445: 'Positioner Primary Angle',
  x00189446: 'Positioner Secondary Angle',
  x00189447: 'Positioner Primary Angle Increment',
  x00189448: 'Positioner Secondary Angle Increment',
  x00189449: 'Detector Primary Angle',
  x0018944a: 'Detector Secondary Angle',
  x0020000d: 'Study Instance UID',
  x0020000e: 'Series Instance UID',
  x00200010: 'Study ID',
  x00200011: 'Series Number',
  x00200012: 'Acquisition Number',
  x00200013: 'Instance Number',
  x00200020: 'Patient Orientation',
  x00200032: 'Image Position (Patient)',
  x00200037: 'Image Orientation (Patient)',
  x00200052: 'Frame of Reference UID',
  x00200100: 'Temporal Position Identifier',
  x00200105: 'Number of Temporal Positions',
  x00200110: 'Temporal Resolution',
  x00201000: 'Series in Study',
  x00201002: 'Images in Acquisition',
  x00201040: 'Position Reference Indicator',
  x00201041: 'Slice Location',
  x00204000: 'Image Comments',
  x00280002: 'Samples per Pixel',
  x00280004: 'Photometric Interpretation',
  x00280006: 'Planar Configuration',
  x00280008: 'Number of Frames',
  x00280009: 'Frame Increment Pointer',
  x00280010: 'Rows',
  x00280011: 'Columns',
  x00280030: 'Pixel Spacing',
  x00280034: 'Pixel Aspect Ratio',
  x00280100: 'Bits Allocated',
  x00280101: 'Bits Stored',
  x00280102: 'High Bit',
  x00280103: 'Pixel Representation',
  x00280106: 'Smallest Image Pixel Value',
  x00280107: 'Largest Image Pixel Value',
  x00280108: 'Smallest Pixel Value in Series',
  x00280109: 'Largest Pixel Value in Series',
  x00281050: 'Window Center',
  x00281051: 'Window Width',
  x00281052: 'Rescale Intercept',
  x00281053: 'Rescale Slope',
  x00281054: 'Rescale Type',
  x00281055: 'Window Center & Width Explanation',
  x00282110: 'Lossy Image Compression',
  x00282112: 'Lossy Image Compression Ratio',
  x00282114: 'Lossy Image Compression Method',
  x00321000: 'Scheduled Study Start Date',
  x00321001: 'Scheduled Study Start Time',
  x00321010: 'Scheduled Study Location',
  x00321060: 'Requested Procedure Description',
  x00324000: 'Study Comments',
  x00400001: 'Scheduled Station AE Title',
  x00400002: 'Scheduled Procedure Step Start Date',
  x00400003: 'Scheduled Procedure Step Start Time',
  x00400007: 'Scheduled Procedure Step Description',
  x00400008: 'Scheduled Protocol Code Sequence',
  x00400009: 'Scheduled Procedure Step ID',
  x00400010: 'Scheduled Station Name',
  x00400011: 'Scheduled Procedure Step Location',
  x00400012: 'Pre-Medication',
  x00400241: 'Performed Station AE Title',
  x00400242: 'Performed Location',
  x00400243: 'Performed Station Name',
  x00400244: 'Performed Procedure Step Start Date',
  x00400245: 'Performed Procedure Step Start Time',
  x00400250: 'Performed Procedure Step End Date',
  x00400251: 'Performed Procedure Step End Time',
  x00400252: 'Performed Procedure Step Status',
  x00400253: 'Performed Procedure Step ID',
  x00400254: 'Performed Procedure Step Description',
  x00400255: 'Performed Procedure Type Description',
  x00400260: 'Performed Protocol Code Sequence',
  x00400270: 'Scheduled Step Attributes Sequence',
  x00400275: 'Request Attributes Sequence',
  x00400280: 'Comments on the Performed Procedure Step',
  x00400300: 'Total Time of Fluoroscopy',
  x00400301: 'Total Number of Exposures',
  x00400302: 'Entrance Dose',
  x00400303: 'Exposed Area',
  x00400306: 'Distance Source to Entrance',
  x0040030e: 'Exposure Dose Sequence',
  x00400310: 'Comments on Radiation Dose',
  x00400312: 'X-Ray Output',
  x00400314: 'Half Value Layer',
  x00400316: 'Organ Dose',
  x00400318: 'Organ Exposed',
  x00400320: 'Billing Procedure Step Sequence',
  x00400321: 'Film Consumption Sequence',
  x00400324: 'Billing Supplies and Devices Sequence',
  x00400340: 'Performed Series Sequence',
  x004003a0: 'Comments on Performed Procedure Step',
  x00400555: 'Acquisition Context Sequence',
  x0040a043: 'Concept Name Code Sequence',
  x0040a160: 'Text Value',
  x0040a168: 'Concept Code Sequence',
  x0040a170: 'Purpose of Reference Code Sequence',
  x0040a300: 'Measured Value Sequence',
  x0040a30a: 'Numeric Value',
  x00540010: 'Energy Window Vector',
  x00540011: 'Number of Energy Windows',
  x00540012: 'Energy Window Information Sequence',
  x00540013: 'Energy Window Range Sequence',
  x00540014: 'Energy Window Lower Limit',
  x00540015: 'Energy Window Upper Limit',
  x00540016: 'Radiopharmaceutical Information Sequence',
  x00540017: 'Residual Syringe Counts',
  x00540018: 'Energy Window Name',
  x00540020: 'Detector Vector',
  x00540021: 'Number of Detectors',
  x00540022: 'Detector Information Sequence',
  x00540030: 'Phase Vector',
  x00540031: 'Number of Phases',
  x00540032: 'Phase Information Sequence',
  x00540033: 'Number of Frames in Phase',
  x00540036: 'Phase Delay',
  x00540038: 'Pause Between Frames',
  x00540039: 'Phase Description',
  x00540050: 'Rotation Vector',
  x00540051: 'Number of Rotations',
  x00540052: 'Rotation Information Sequence',
  x00540053: 'Number of Frames in Rotation',
  x00540060: 'R-R Interval Vector',
  x00540061: 'Number of R-R Intervals',
  x00540062: 'Gated Information Sequence',
  x00540063: 'Data Information Sequence',
  x00540070: 'Time Slot Vector',
  x00540071: 'Number of Time Slots',
  x00540072: 'Time Slot Information Sequence',
  x00540073: 'Time Slot Time',
  x00540080: 'Slice Vector',
  x00540081: 'Number of Slices',
  x00540090: 'Angular View Vector',
  x00540100: 'Time Slice Vector',
  x00540101: 'Number of Time Slices',
  x00540200: 'Start Angle',
  x00540202: 'Type of Detector Motion',
  x00540210: 'Trigger Vector',
  x00540211: 'Number of Triggers in Phase',
  x00540220: 'View Code Sequence',
  x00540222: 'View Modifier Code Sequence',
  x00700041: 'Image Horizontal Flip',
  x00700042: 'Image Rotation',
  x7fe00010: 'Pixel Data',
}

const GROUP_TITLES = {
  '0002': 'File Meta',
  '0004': 'DICOMDIR',
  '0008': 'Identificación',
  '0010': 'Paciente',
  '0012': 'Ensayo clínico',
  '0018': 'Adquisición',
  '0020': 'Relación / estudio',
  '0028': 'Píxel / imagen',
  '0032': 'Estudio',
  '0038': 'Visita',
  '0040': 'Procedimiento / dosis',
  '0050': 'Calibración',
  '0054': 'NM / PET',
  '0060': 'Histogram',
  '0070': 'Presentación',
  '0088': 'Storage',
  '0100': 'SOP Common',
  '0400': 'Firma digital',
  '2000': 'Film Session',
  '2010': 'Film Box',
  '2020': 'Image Box',
  '2030': 'Annotation',
  '2040': 'Overlay Box',
  '2050': 'Presentation LUT',
  '2100': 'Print Job',
  '2110': 'Printer',
  '2120': 'Printer Queue',
  '2130': 'Print',
  '2200': 'Media Creation',
  '3002': 'RT Image',
  '3004': 'RT Dose',
  '3006': 'RT Structure Set',
  '3008': 'RT Treatment',
  '300a': 'RT Plan',
  '300c': 'RT Relationship',
  '4000': 'Text',
  '4008': 'Results',
  '4ffe': 'MAC Parameters',
  '50xx': 'Curve',
  '5200': 'Functional Groups',
  '5400': 'Waveform',
  '5600': 'Spectroscopy',
  '60xx': 'Overlay',
  '7fe0': 'Pixel Data',
  fffc: 'Data Set Trailing Padding',
  fffe: 'Item / Delimitation',
}

const SKIP_BINARY = new Set(['x7fe00010', 'x7fe00008', 'x7fe00009'])
const MAX_VALUE_LEN = 240
const MAX_NESTED = 80

function padTag(tag) {
  const t = String(tag || '').toLowerCase().replace(/^x/, '')
  return t.length === 8 ? t : null
}

export function formatTagId(tag) {
  const t = padTag(tag)
  if (!t) return String(tag || '')
  return `(${t.slice(0, 4).toUpperCase()},${t.slice(4).toUpperCase()})`
}

function groupKey(tag) {
  const t = padTag(tag)
  if (!t) return '????'
  const g = t.slice(0, 4)
  const gn = parseInt(g, 16)
  if (Number.isFinite(gn) && gn % 2 === 1) return 'priv'
  if (g[0] === '5' && g[1] === '0') return '50xx'
  if (g[0] === '6' && g[1] === '0') return '60xx'
  return g
}

export function groupTitle(tag) {
  const g = groupKey(tag)
  if (g === 'priv') return 'Privados'
  return GROUP_TITLES[g] || `Grupo ${g.toUpperCase()}`
}

function truncate(s) {
  if (s == null) return ''
  const str = String(s)
  if (str.length <= MAX_VALUE_LEN) return str
  return `${str.slice(0, MAX_VALUE_LEN)}…`
}

function readElementValue(dataSet, tag, el) {
  if (!el) return ''
  const tagKey = String(tag).toLowerCase()
  if (tagKey === 'x00020010') {
    try {
      const uid =
        (typeof dataSet.string === 'function' && dataSet.string(tagKey)) || ''
      const ts = resolveTransferSyntax(uid)
      if (ts.uid) {
        return `${ts.summary} · ${ts.uid}${ts.name ? ` · ${ts.name}` : ''}`
      }
    } catch (e) {
      /* fall through */
    }
  }
  if (SKIP_BINARY.has(tagKey)) {
    const len = el.length
    if (len === 0xffffffff) return '[Pixel Data · encapsulado]'
    return `[binario · ${len} bytes]`
  }
  if (el.items && el.items.length) {
    return `Sequence · ${el.items.length} ítem(s)`
  }
  const vr = (el.vr || '').toUpperCase()
  try {
    if (vr === 'SQ') return 'Sequence'
    if (typeof dataSet.text === 'function') {
      const t = dataSet.text(tag)
      if (t != null && t !== '') return truncate(t)
    }
    if (typeof dataSet.string === 'function') {
      const s = dataSet.string(tag)
      if (s != null && s !== '') return truncate(s)
    }
    if (vr === 'US' || vr === 'SS' || vr === 'OW' || vr === 'OL') {
      if (typeof dataSet.uint16 === 'function') {
        const n = dataSet.uint16(tag)
        if (n != null) return String(n)
      }
    }
    if (vr === 'UL' || vr === 'SL' || vr === 'AT') {
      if (typeof dataSet.uint32 === 'function') {
        const n = dataSet.uint32(tag)
        if (n != null) return String(n)
      }
    }
    if (vr === 'FL' && typeof dataSet.float === 'function') {
      const n = dataSet.float(tag)
      if (n != null) return String(n)
    }
    if (vr === 'FD' && typeof dataSet.double === 'function') {
      const n = dataSet.double(tag)
      if (n != null) return String(n)
    }
  } catch (e) {
    /* ignore */
  }
  if (el.length === 0) return ''
  if (el.length > 0 && el.length < 64 && dataSet.byteArray && el.dataOffset != null) {
    try {
      const slice = dataSet.byteArray.slice(el.dataOffset, el.dataOffset + Math.min(el.length, 48))
      return truncate(Array.from(slice).map((b) => b.toString(16).padStart(2, '0')).join(' '))
    } catch (e) {
      /* ignore */
    }
  }
  return el.length > 0 ? `[${el.length} bytes]` : ''
}

function tagName(tag) {
  const key = String(tag).toLowerCase()
  return DICOM_TAG_NAMES[key] || null
}

function pushElement(rows, dataSet, tag, el, depth, parentPath) {
  const key = String(tag).toLowerCase()
  const name = tagName(key) || (groupKey(key) === 'priv' ? 'Private Tag' : 'Unknown Tag')
  const vr = (el && el.vr) || ''
  const value = readElementValue(dataSet, key, el)
  const id = formatTagId(key)
  rows.push({
    tag: key,
    id,
    name,
    vr,
    value,
    group: groupTitle(key),
    depth,
    path: parentPath ? `${parentPath}/${id}` : id,
    search: `${id} ${name} ${vr} ${value} ${groupTitle(key)}`.toLowerCase(),
  })

  if (el && el.items && el.items.length && depth < 1) {
    let nested = 0
    for (let i = 0; i < el.items.length && nested < MAX_NESTED; i++) {
      const item = el.items[i]
      const itemDs = item && item.dataSet
      if (!itemDs || !itemDs.elements) continue
      const itemTags = Object.keys(itemDs.elements).sort()
      for (let j = 0; j < itemTags.length && nested < MAX_NESTED; j++) {
        const it = itemTags[j]
        pushElement(rows, itemDs, it, itemDs.elements[it], depth + 1, `${id}[${i}]`)
        nested++
      }
    }
  }
}

/**
 * @param {object} dataSet dicom-parser DataSet (image.data)
 * @returns {Array<object>}
 */
export function dumpDicomDataset(dataSet) {
  if (!dataSet || !dataSet.elements) return []
  const tags = Object.keys(dataSet.elements).sort()
  const rows = []
  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    pushElement(rows, dataSet, tag, dataSet.elements[tag], 0, '')
  }
  return rows
}

/** Orden de grupos para la UI */
export const GROUP_ORDER = [
  'File Meta',
  'Identificación',
  'Paciente',
  'Ensayo clínico',
  'Adquisición',
  'Relación / estudio',
  'Píxel / imagen',
  'Estudio',
  'Visita',
  'Procedimiento / dosis',
  'NM / PET',
  'Presentación',
  'Functional Groups',
  'RT Image',
  'RT Dose',
  'RT Structure Set',
  'RT Treatment',
  'RT Plan',
  'Privados',
  'Pixel Data',
]
